// T1 · Saisie — grille catalogue tactile, panier avec modificateurs D6, mémoire
// client (téléphone), « Première disponibilité estimée » (moteur heure-capacite
// LOT 7 §3 — l'ancien calcul d'heure v1 n'est plus référencé par l'UI) + ajustement manuel
// ±5, garde de promesse JAMAIS bloquante au Valider (§3.4). Accès données 100 %
// via hooks/wrappers (INV-C). Téléphone : jamais loggé, jamais dans l'URL/hash ;
// autocomplete off, inputmode tel (RGPD §8, T1 seul écran).
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useCatalogue } from '@/hooks/useCatalogue';
import { useFileCommandes } from '@/hooks/useFileCommandes';
import {
  createCommande,
  createCommandeSansClient,
  findClientByTelephone,
} from '@/db/transactions';
import { createCommandeSansClientAvecPrenom } from '@/db/commande-prenom';
import {
  evalPromesse,
  heureProposee as heureProposeeMoteur,
  premiereDispo,
  versChargeCommandes,
} from '@/lib/heure-capacite';
import { composeNotes } from '@/lib/notes-convention';
import { formatHeure } from '@/lib/format';
import {
  AJUSTEMENT_PAS_MIN,
  CAMION_CAPACITE_CONFIG,
} from '@/config/camion.pizznco';
import {
  FAMILLES_CARTE,
  NOM_SUR_MESURE,
} from '@/config/ordre-carte.pizznco';
import type { Client, Pizza } from '@/db/schema';
import {
  ModificateursDialog,
  type ModificateursValue,
} from './ModificateursDialog';
import { GardePromesseOverlay } from './GardePromesseOverlay';
import { PorteeModifOverlay } from './PorteeModifOverlay';

interface LignePanier {
  pizzaId: number;
  nom: string;
  prix_unitaire_indicatif: number;
  qty: number;
  mod: string[];
  texte: string;
}

const MS_MIN = 60_000;

// Signature d'une ligne (LOT 7 §4) : pizzaId + mods + note. Le re-merge du
// panier n'a lieu QUE si la signature est identique (ordre des mods indifférent).
function signatureLigne(l: Pick<LignePanier, 'pizzaId' | 'mod' | 'texte'>): string {
  return `${l.pizzaId}|${[...l.mod].sort((a, b) => a.localeCompare(b)).join('\u001f')}|${l.texte}`;
}

// Re-merge : fusionne les lignes de signature identique (Σ qty), en conservant
// la position de la PREMIÈRE occurrence (positions stables au panier).
function normaliserPanier(lignes: readonly LignePanier[]): LignePanier[] {
  const parSignature = new Map<string, LignePanier>();
  const resultat: LignePanier[] = [];
  for (const l of lignes) {
    const sig = signatureLigne(l);
    const existante = parSignature.get(sig);
    if (existante) {
      existante.qty += l.qty;
    } else {
      const copie = { ...l };
      parSignature.set(sig, copie);
      resultat.push(copie);
    }
  }
  return resultat;
}

// Payload remonté à la validation — alimente l'écran de confirmation (LOT 7 §1).
export interface ValidationInfo {
  nbPizzas: number;
  heure: number;
}

export function SaisieView({
  onValide,
}: {
  onValide: (info: ValidationInfo) => void;
}) {
  const catalogue = useCatalogue();
  const file = useFileCommandes();

  const [lignes, setLignes] = useState<LignePanier[]>([]);
  const [telephone, setTelephone] = useState('');
  const [prenom, setPrenom] = useState('');
  const [clientConnu, setClientConnu] = useState<Client | null>(null);
  const [rechercheFaite, setRechercheFaite] = useState(false);
  const [ajustementMin, setAjustementMin] = useState(0);
  const [dialogIndex, setDialogIndex] = useState(-1);
  // Éclatement §4 : portée de la modification en cours ('une' = split) et
  // ligne en attente du choix de portée (qty > 1 uniquement).
  const [porteeModif, setPorteeModif] = useState<'une' | 'toutes'>('toutes');
  const [choixPorteeIndex, setChoixPorteeIndex] = useState(-1);
  // Garde de promesse (§3.4) : première dispo estimée au moment du Valider
  // intenable — aucune écriture tant que Coralie n'a pas tranché.
  const [gardeDispo, setGardeDispo] = useState<number | null>(null);

  const nbPizzas = lignes.reduce((s, l) => s + l.qty, 0);

  // Moteur créneaux/capacité (§3, séparation binding revue 7b) :
  // - première disponibilité estimée = plus tôt techniquement possible ;
  // - heure PROPOSÉE (pré-remplie) = première dispo + 1 créneau de sécurité
  //   (dérivée de la config) — la commande naît « À l'heure ».
  const chargeCommandes = useMemo(
    () => versChargeCommandes(file.commandes, file.lignesParCommande),
    [file],
  );
  const { dispoEstimee, proposee } = useMemo(() => {
    const now = Date.now();
    return {
      dispoEstimee: premiereDispo(
        now,
        chargeCommandes,
        CAMION_CAPACITE_CONFIG,
        nbPizzas,
      ),
      proposee: heureProposeeMoteur(
        now,
        chargeCommandes,
        CAMION_CAPACITE_CONFIG,
        nbPizzas,
      ),
    };
  }, [chargeCommandes, nbPizzas]);
  const heureEffective = proposee + ajustementMin * MS_MIN;

  const ajouterPizza = (p: Pizza): void => {
    // Merge UNIQUEMENT à signature identique (§4) : un tap catalogue = ligne
    // nue (sans mod/note) — il ne re-fusionne jamais avec une ligne modifiée.
    setLignes((prev) =>
      normaliserPanier([
        ...prev,
        {
          pizzaId: p.id as number,
          nom: p.nom,
          prix_unitaire_indicatif: p.prix_indicatif,
          qty: 1,
          mod: [],
          texte: '',
        },
      ]),
    );
  };

  const changerQty = (index: number, delta: number): void => {
    setLignes((prev) =>
      prev
        .map((l, i) => (i === index ? { ...l, qty: l.qty + delta } : l))
        .filter((l) => l.qty > 0),
    );
  };

  // Applique la modification selon la portée (§4). 'une' sur qty > 1 = SPLIT :
  // ligne qty−1 inchangée + ligne qty 1 modifiée. Puis re-merge par signature
  // (si la valeur revient à l'identique, les lignes re-fusionnent d'elles-mêmes).
  const appliquerMod = (
    index: number,
    value: ModificateursValue,
    portee: 'une' | 'toutes',
  ): void => {
    setLignes((prev) => {
      const ligne = prev[index];
      if (!ligne) return [...prev];
      if (portee === 'une' && ligne.qty > 1) {
        const reste: LignePanier = { ...ligne, qty: ligne.qty - 1 };
        const une: LignePanier = { ...ligne, ...value, qty: 1 };
        return normaliserPanier([
          ...prev.slice(0, index),
          reste,
          une,
          ...prev.slice(index + 1),
        ]);
      }
      return normaliserPanier(
        prev.map((l, i) => (i === index ? { ...l, ...value } : l)),
      );
    });
  };

  // Tap « Modifs » : qty > 1 -> choix de portée d'abord ; sinon dialog direct.
  const ouvrirModifs = (index: number): void => {
    if ((lignes[index]?.qty ?? 0) > 1) {
      setChoixPorteeIndex(index);
    } else {
      setPorteeModif('toutes');
      setDialogIndex(index);
    }
  };

  const verifierMemoire = async (): Promise<void> => {
    const tel = telephone.trim();
    if (tel.length === 0) return;
    setClientConnu(await findClientByTelephone(tel));
    setRechercheFaite(true);
  };

  const reset = (): void => {
    setLignes([]);
    setTelephone('');
    setPrenom('');
    setClientConnu(null);
    setRechercheFaite(false);
    setAjustementMin(0);
  };

  // Écriture effective (les deux issues de la garde y passent aussi).
  const persister = async (heureRetrait: number): Promise<void> => {
    const pizzas = lignes.map((l) => {
      const notes = composeNotes(l.texte, { mod: l.mod });
      return {
        pizzaId: l.pizzaId,
        qty: l.qty,
        prix_unitaire_indicatif: l.prix_unitaire_indicatif,
        ...(notes.length > 0 ? { notes_libres: notes } : {}),
      };
    });
    const tel = telephone.trim();
    const nom = prenom.trim();
    if (tel.length > 0) {
      await createCommande({
        telephone: tel,
        ...(nom.length > 0 ? { nom } : {}),
        pizzas,
        heure_retrait_proposee: heureRetrait,
      });
    } else if (nom.length > 0) {
      // Prénom sans téléphone : aucune fiche Client, snapshot d'affichage seul.
      await createCommandeSansClientAvecPrenom({
        prenom: nom,
        pizzas,
        heure_retrait_proposee: heureRetrait,
      });
    } else {
      await createCommandeSansClient({
        pizzas,
        heure_retrait_proposee: heureRetrait,
      });
    }
    const n = nbPizzas;
    setGardeDispo(null);
    reset();
    onValide({ nbPizzas: n, heure: heureRetrait });
  };

  const valider = async (): Promise<void> => {
    if (lignes.length === 0) return;
    // Garde de promesse (§3.4) — jamais bloquante : heure choisie < première
    // dispo estimée -> overlay, Coralie décide.
    const now = Date.now();
    const verdict = evalPromesse(
      now,
      chargeCommandes,
      CAMION_CAPACITE_CONFIG,
      heureEffective,
      nbPizzas,
    );
    if (verdict === 'intenable') {
      setGardeDispo(
        premiereDispo(now, chargeCommandes, CAMION_CAPACITE_CONFIG, nbPizzas),
      );
      return;
    }
    await persister(heureEffective);
  };

  return (
    <div className="flex flex-col gap-6 p-4">
      {/* Catalogue tactile XXL — BLOCS DE FAMILLES visibles (complément 7c) :
          titres statiques verbatim TD §5, ORDRE_CARTE inchangé (dérivé des
          familles), positions stables, zéro tri dynamique, zéro animation.
          Prix sur mesure JAMAIS affiché (seed non fiable — arbitrage Coralie). */}
      <section aria-label="Catalogue" className="flex flex-col gap-4">
        {FAMILLES_CARTE.map((famille) => {
          const parNom = new Map(catalogue.map((p) => [p.nom, p]));
          const pizzasFamille = famille.noms
            .map((nom) => parNom.get(nom))
            .filter((p): p is Pizza => p !== undefined);
          if (pizzasFamille.length === 0) return null;
          return (
            <div key={famille.titre} className="flex flex-col gap-2">
              <h3 className="font-jost text-xl font-bold">{famille.titre}</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {pizzasFamille.map((p) => (
                  <Button
                    key={p.id}
                    type="button"
                    variant="secondary"
                    onClick={() => ajouterPizza(p)}
                    className="min-h-[120px] flex-col whitespace-normal text-lg"
                  >
                    <span className="font-jost font-bold">{p.nom}</span>
                    <span className="font-mono text-sm">
                      {p.nom === NOM_SUR_MESURE
                        ? 'prix à confirmer'
                        : `${p.prix_indicatif.toFixed(2).replace('.', ',')} €`}
                    </span>
                  </Button>
                ))}
              </div>
            </div>
          );
        })}
      </section>

      {/* Panier. */}
      <section aria-label="Commande en cours" className="flex flex-col gap-2">
        {lignes.length === 0 && (
          <p className="font-jost text-muted-foreground">Aucune pizza ajoutée.</p>
        )}
        {lignes.map((l, i) => (
          <Card key={`${l.pizzaId}-${i}`}>
            <CardContent className="flex items-center justify-between gap-3 py-3">
              <div className="flex flex-col">
                <span className="font-jost text-lg font-medium">{l.nom}</span>
                {(l.mod.length > 0 || l.texte.length > 0) && (
                  <span className="font-mono text-xs text-muted-foreground">
                    {l.mod.join(' ')}
                    {l.texte ? ` · ${l.texte}` : ''}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={`Retirer une ${l.nom}`}
                  onClick={() => changerQty(i, -1)}
                  className="min-h-[64px] min-w-[64px]"
                >
                  −
                </Button>
                <span className="font-mono text-xl" aria-label="Quantité">
                  {l.qty}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={`Ajouter une ${l.nom}`}
                  onClick={() => changerQty(i, 1)}
                  className="min-h-[64px] min-w-[64px]"
                >
                  +
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => ouvrirModifs(i)}
                  className="min-h-[64px]"
                >
                  Modifs
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Client (prénom + téléphone) + bandeau mémoire. */}
      <section aria-label="Client" className="flex flex-col gap-2">
        <div className="flex items-end gap-2">
          <label className="flex flex-1 flex-col gap-1">
            <span className="font-jost text-sm">Prénom (facultatif)</span>
            <Input
              value={prenom}
              onChange={(e) => setPrenom(e.target.value)}
              autoComplete="off"
              placeholder="Camille"
            />
          </label>
          <label className="flex flex-1 flex-col gap-1">
            <span className="font-jost text-sm">Téléphone (facultatif)</span>
            <Input
              value={telephone}
              onChange={(e) => {
                setTelephone(e.target.value);
                setRechercheFaite(false);
                setClientConnu(null);
              }}
              autoComplete="off"
              inputMode="tel"
              placeholder="06 12 34 56 78"
            />
          </label>
          <Button
            type="button"
            variant="outline"
            onClick={verifierMemoire}
            className="min-h-[64px]"
          >
            Mémoire
          </Button>
        </div>
        {rechercheFaite && clientConnu && (
          <Card className="bg-accent/30">
            <CardContent className="py-3">
              <p className="font-cormorant text-2xl italic">
                {clientConnu.nom || 'Client connu'}
              </p>
              {clientConnu.habitude && (
                <p className="font-cormorant text-lg italic">
                  ☆ {clientConnu.habitude.slice(0, 60)}
                </p>
              )}
              {clientConnu.commandes_recurrentes && (
                <p className="font-jost text-sm text-muted-foreground">
                  {clientConnu.commandes_recurrentes}
                </p>
              )}
            </CardContent>
          </Card>
        )}
        {rechercheFaite && !clientConnu && (
          <p className="font-jost text-muted-foreground">Nouveau client.</p>
        )}
      </section>

      {/* Heure proposée (pré-remplie, = 1re dispo + 1 créneau) + mention 1re
          dispo + ajustement manuel. Toujours une ESTIMATION, jamais une
          garantie (§3.3). */}
      <section aria-label="Heure" className="flex flex-col gap-2">
        <p className="font-jost text-lg">
          Heure proposée par Tablier :{' '}
          <span className="font-mono font-medium" data-testid="heure-proposee">
            {formatHeure(heureEffective)}
          </span>
        </p>
        <p className="font-jost text-sm text-muted-foreground">
          1re dispo estimée :{' '}
          <span className="font-mono" data-testid="premiere-dispo">
            {formatHeure(dispoEstimee)}
          </span>
        </p>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            aria-label="Retirer 5 minutes"
            onClick={() => setAjustementMin((a) => a - AJUSTEMENT_PAS_MIN)}
            className="min-h-[80px] min-w-[80px] text-lg"
          >
            −5 min
          </Button>
          <Button
            type="button"
            variant="outline"
            aria-label="Ajouter 5 minutes"
            onClick={() => setAjustementMin((a) => a + AJUSTEMENT_PAS_MIN)}
            className="min-h-[80px] min-w-[80px] text-lg"
          >
            +5 min
          </Button>
        </div>
      </section>

      <Button
        type="button"
        onClick={valider}
        disabled={lignes.length === 0}
        className="min-h-[96px] text-2xl"
      >
        Valider la commande
      </Button>

      {gardeDispo !== null && (
        <GardePromesseOverlay
          choisie={heureEffective}
          dispo={gardeDispo}
          onPrendre={() => void persister(gardeDispo)}
          onGarder={() => void persister(heureEffective)}
        />
      )}

      {choixPorteeIndex >= 0 && lignes[choixPorteeIndex] && (
        <PorteeModifOverlay
          nom={lignes[choixPorteeIndex]!.nom}
          qty={lignes[choixPorteeIndex]!.qty}
          onUneSeule={() => {
            setPorteeModif('une');
            setDialogIndex(choixPorteeIndex);
            setChoixPorteeIndex(-1);
          }}
          onToutes={() => {
            setPorteeModif('toutes');
            setDialogIndex(choixPorteeIndex);
            setChoixPorteeIndex(-1);
          }}
          onRetour={() => setChoixPorteeIndex(-1)}
        />
      )}

      {dialogIndex >= 0 && lignes[dialogIndex] && (
        <ModificateursDialog
          open={dialogIndex >= 0}
          onOpenChange={(o) => !o && setDialogIndex(-1)}
          titre={lignes[dialogIndex]!.nom}
          labelAjouts={
            lignes[dialogIndex]!.nom === NOM_SUR_MESURE
              ? 'Composition'
              : 'Ajouts'
          }
          value={{
            mod: lignes[dialogIndex]!.mod,
            texte: lignes[dialogIndex]!.texte,
          }}
          onSave={(v) => appliquerMod(dialogIndex, v, porteeModif)}
        />
      )}
    </div>
  );
}
